import { Component, ElementRef, Inject, Injector, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, AutomaticResponseUtil, FirebaseService, MessageTypeEnum, UploadTypeEnum } from 'lib-trend-core';
import { formatChatFileSize } from 'lib-trend-core/chat';
import { ResponseMessageUtil, ResponseTextFormat } from '../response-message.util';

export interface ResponseAttachment {
  midia: string;
  contentType: string;
  filename: string;
  filesize?: number;
}

export interface ResponseCaptionDialogData {
  responseName: string;
  channelType: string;
  message: string;
  // A file just picked, uploaded on apply, or the attachment the response already has.
  file?: File;
  attachment?: ResponseAttachment;
}

export interface ResponseCaptionDialogResult {
  attachment: ResponseAttachment;
  message: string;
  uploaded: boolean;
}

/**
 * Caption step of an automatic response attachment, as the attendance does after a file is picked:
 * preview, caption (the response text) and apply. A picked file is uploaded only on apply, so
 * cancelling leaves nothing in storage.
 */
@Component({
  selector: 'response-caption-dialog-component',
  templateUrl: './response-caption-dialog.component.html',
  styleUrls: ['./response-caption-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ResponseCaptionDialogComponent extends AbstractComponent {

  @ViewChild('captionInput', { static: true }) captionInput: ElementRef<HTMLTextAreaElement>;

  readonly limit: number | null;
  readonly isImage: boolean;
  readonly previewUrl: string;
  readonly filename: string;
  readonly size: string | null;

  message: string;
  showEmoji = false;
  uploading = false;

  private readonly objectUrl: string | null;

  constructor(
    injector: Injector,
    private firebaseService: FirebaseService,
    private dialogRef: MatDialogRef<ResponseCaptionDialogComponent, ResponseCaptionDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: ResponseCaptionDialogData,
  ) {
    super(injector);
    const view = ResponseCaptionDialogComponent.viewOf(data);
    this.objectUrl = data.file ? view.midia : null;
    this.previewUrl = view.midia;
    this.filename = view.filename ?? '';
    this.size = view.filesize ? formatChatFileSize(view.filesize) : null;
    this.isImage = AutomaticResponseUtil.attachmentType(view.contentType) === MessageTypeEnum.image;
    this.limit = AutomaticResponseUtil.captionLimit(data.channelType);
    this.message = data.message ?? '';
  }

  // What the dialog shows: the file just picked, previewed from memory, or the saved attachment.
  private static viewOf(data: ResponseCaptionDialogData): Partial<ResponseAttachment> {
    const file = data.file;
    return file
      ? { midia: URL.createObjectURL(file), contentType: file.type, filename: file.name, filesize: file.size }
      : { ...data.attachment };
  }

  get excess(): number {
    return this.limit === null ? 0 : Math.max(0, this.message.length - this.limit);
  }

  get subtitle(): string {
    const name = this.t('response.automatic.caption.subtitle', { name: this.data.responseName });
    return [name, this.filename, this.size].filter(Boolean).join(' · ');
  }

  get help(): string {
    return this.t(this.isImage ? 'response.automatic.caption.imageHelp' : 'response.automatic.caption.documentHelp');
  }

  get extension(): string {
    return AutomaticResponseUtil.extension(this.filename);
  }

  applyFormat(format: ResponseTextFormat): void {
    const textarea = this.captionInput.nativeElement;
    const formatted = ResponseMessageUtil.format(this.message, textarea.selectionStart, textarea.selectionEnd, format);
    if (!formatted || !this.accepts(formatted.message)) return;
    this.message = formatted.message;
    setTimeout(() => {
      textarea.setSelectionRange(formatted.caret, formatted.caret);
      textarea.focus();
    });
  }

  addVariable(variable: string): void {
    const message = ResponseMessageUtil.appendVariable(this.message, variable);
    if (this.accepts(message)) {
      this.message = message;
    }
  }

  addEmoji(event: any): void {
    const message = ResponseMessageUtil.appendEmoji(this.message, event.emoji.native);
    if (this.accepts(message)) {
      this.message = message;
    }
    this.showEmoji = false;
  }

  close(): void {
    this.dialogRef.close();
  }

  async apply(): Promise<void> {
    if (this.uploading || this.excess > 0) return;
    const attachment = this.data.file ? await this.upload(this.data.file) : this.data.attachment;
    if (attachment) {
      this.dialogRef.close({ attachment, message: this.message, uploaded: !!this.data.file });
    }
  }

  override ngOnDestroy(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
    super.ngOnDestroy();
  }

  // Emoji, variables and formatting respect the caption limit, as typing and pasting do.
  private accepts(message: string): boolean {
    if (ResponseMessageUtil.fits(message, this.message, this.limit)) return true;
    this.alertService.warning(this.t('response.automatic.captionLimitReached', { limit: this.limit }));
    return false;
  }

  private async upload(file: File): Promise<ResponseAttachment | null> {
    this.setUploading(true);
    try {
      const filename = `${this.getIDCurrentCompany()}/${new Date().getTime()}`;
      const snapshot: { url: string } = await this.firebaseService.uploadFile(file, filename, UploadTypeEnum.RESPONSE);
      return { midia: snapshot.url, contentType: file.type, filename: file.name, filesize: file.size };
    } catch {
      this.alertService.error(this.t('response.automatic.attachment.uploadError'));
      return null;
    } finally {
      this.setUploading(false);
    }
  }

  private setUploading(uploading: boolean): void {
    this.uploading = uploading;
    this.dialogRef.disableClose = uploading;
  }
}
